import { router } from '@inertiajs/react'
import { Check, Copy, ExternalLink, Globe, Lock } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { useToast } from '@/hooks/use-toast'
import {
  organizationRepositoryPublicViewPath,
  publicChangelogOwnerPath,
} from '@/routes'
import type { OrganizationsPublicProfileRepository } from '@/types'

type PublicProfileSettingsProps = {
  organizationId: string
  repositories: OrganizationsPublicProfileRepository[]
  organizationOwner: string | null
  mutationsEnabled: boolean
}

export function PublicProfileSettings({
  organizationId,
  repositories,
  organizationOwner,
  mutationsEnabled,
}: PublicProfileSettingsProps) {
  const { toast } = useToast()
  const [repoStates, setRepoStates] = useState<Record<string, boolean>>(
    Object.fromEntries(repositories.map((repo) => [repo.id, repo.isPublicView])),
  )
  const [pendingRepos, setPendingRepos] = useState<Set<string>>(new Set())
  const [copied, setCopied] = useState(false)

  const hasPublicRepos = Object.values(repoStates).some(Boolean)
  const publicProfileUrl =
    organizationOwner && typeof window !== 'undefined'
      ? `${window.location.origin}${publicChangelogOwnerPath(organizationOwner)}`
      : organizationOwner
        ? publicChangelogOwnerPath(organizationOwner)
        : null

  const handleCopy = async () => {
    if (!publicProfileUrl) return

    try {
      await navigator.clipboard.writeText(publicProfileUrl)
      setCopied(true)
      toast({ description: 'Link copied to clipboard', variant: 'success' })
      setTimeout(() => setCopied(false), 2000)
    } catch {
      toast({ description: 'Failed to copy link', variant: 'destructive' })
    }
  }

  const handleToggle = (repo: OrganizationsPublicProfileRepository, checked: boolean) => {
    if (!mutationsEnabled) return

    const previous = repoStates[repo.id]
    setRepoStates((prev) => ({ ...prev, [repo.id]: checked }))
    setPendingRepos((prev) => new Set(prev).add(repo.id))

    router.patch(
      organizationRepositoryPublicViewPath(organizationId, repo.owner, repo.name),
      { repository: { is_public_view: checked } },
      {
        preserveScroll: true,
        onFinish: () => {
          setPendingRepos((prev) => {
            const next = new Set(prev)
            next.delete(repo.id)
            return next
          })
        },
        onError: () => {
          setRepoStates((prev) => ({ ...prev, [repo.id]: previous }))
          toast({
            description: 'Could not update changelog visibility. Please try again.',
            variant: 'destructive',
          })
        },
      },
    )
  }

  if (repositories.length === 0) {
    return (
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardDescription>
            Add repositories in Settings first, then enable public visibility here.
          </CardDescription>
        </CardHeader>
      </Card>
    )
  }

  return (
    <Card className="w-full max-w-md">
      <CardHeader>
        <CardDescription>
          Share your organization&apos;s activity with the world. Enable public visibility for
          repositories to include them in your public profile.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {!mutationsEnabled ? (
          <p className="text-muted-foreground text-center text-sm">
            Public changelog publishing is read-only until rollout is enabled on this environment.
          </p>
        ) : null}

        {publicProfileUrl && hasPublicRepos ? (
          <div className="space-y-2">
            <label className="text-sm font-medium">Organization Profile URL</label>
            <div className="flex items-center gap-2">
              <Input
                type="text"
                readOnly
                value={publicProfileUrl}
                className="bg-muted font-mono text-sm"
                aria-label="Public profile URL"
              />
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={handleCopy}
                aria-label="Copy to clipboard"
                className="shrink-0"
              >
                {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
              </Button>
              <Button type="button" variant="outline" size="icon" asChild className="shrink-0">
                <a href={publicProfileUrl} target="_blank" rel="noopener noreferrer" aria-label="Open profile">
                  <ExternalLink className="size-4" />
                </a>
              </Button>
            </div>
          </div>
        ) : null}

        {!hasPublicRepos ? (
          <div className="bg-muted/50 rounded-lg p-4 text-center">
            <p className="text-muted-foreground text-sm">
              Enable at least one repository&apos;s public visibility to get your organization profile
              URL.
            </p>
          </div>
        ) : null}

        <div className="space-y-3">
          <label className="text-sm font-medium">Repository Visibility</label>
          <div className="space-y-2">
            {repositories.map((repo) => (
              <div
                key={repo.id}
                className="bg-muted/30 flex items-center justify-between rounded-lg px-4 py-3 transition-colors"
              >
                <div className="flex items-center gap-3">
                  {repoStates[repo.id] ? (
                    <Globe className="text-primary size-4" />
                  ) : (
                    <Lock className="text-muted-foreground size-4" />
                  )}
                  <span className="text-sm font-medium">{repo.name}</span>
                </div>
                <Switch
                  checked={repoStates[repo.id]}
                  onCheckedChange={(checked) => handleToggle(repo, checked)}
                  disabled={!mutationsEnabled || pendingRepos.has(repo.id)}
                  aria-label={`Toggle public visibility for ${repo.name}`}
                />
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
